import { FC, RefObject, useState } from 'react';
import { Dropdown, Menu, Popconfirm, Tooltip } from 'antd';
import { DeleteOutlined, EditOutlined, MoreOutlined } from '@ant-design/icons';
import { UserResponse } from 'src/types';
import { LuReply } from 'react-icons/lu';
import { getMentionUuidFromNode } from 'src/utils/stringHelpers';
import { UploadFileApi } from 'src/types';
import { RichTextEditorHandle } from '../RichTextEditor';
import CommentReactionsCounter from '../CommentReactionsCounter';
import CommentReactionOptions from '../CommentReactionOptions';
import CommentQuickReactions from '../CommentQuickReactions';
import { BiChat } from 'react-icons/bi';

interface CommentActionsBaseProps {
  comment: any;
  user: UserResponse;
  images: UploadFileApi[];
  loadingImages: number;
  editing: boolean;
  quotedRemoved?: boolean;
  editingText: string;
  editorText: RefObject<RichTextEditorHandle>;
  onReplyTo: (replyToUuid: string, replyTo: any, creator?: any) => void;
  onQuoteTo: (uuid: string, comment: any) => void;
  onEdit: (edit: boolean) => void;
}

export interface CommentActionsParentProps extends CommentActionsBaseProps {
  parentEl?: undefined;
  onAddReaction: (uuid: string, reaction: any) => void;
  onRemoveReaction: (uuid: string, reaction: any) => void;
  onUpdate: (
    uuid: string,
    content: { text: string; delta: string; quoted_id?: null; uploads?: string[] },
    mentions: string[],
    onFinish: () => void
  ) => Promise<void>;
  onDelete: (uuid: string) => Promise<void>;
}

export interface CommentActionsChildrenProps extends CommentActionsBaseProps {
  parentEl: Record<string, any>;
  onAddReaction: (parentUuid: string, uuid: any, reaction: any) => void;
  onRemoveReaction: (parentUuid: string, uuid: any, reaction: any) => void;
  onUpdate: (
    uuid: string,
    parentUuid: string,
    content: { text: string; delta: string; quoted_id?: null; uploads?: string[] },
    mentions: string[],
    onFinish: () => void
  ) => Promise<void>;
  onDelete: (uuid: string, parentUuid: string) => Promise<void>;
}

export type CommentActionsProps = CommentActionsParentProps | CommentActionsChildrenProps;

const CommentActions: FC<CommentActionsProps> = (props) => {
  const { comment, user, editing, quotedRemoved, editingText, editorText, onReplyTo, onQuoteTo, onEdit, images, loadingImages } = props;

  const isOwner = user?.uuid === comment.creator.uuid;
  const [isReactionPopoverOpen, setIsReactionPopoverOpen] = useState(false);
  const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const isBarPinned = isReactionPopoverOpen || isMoreDropdownOpen || isDeleteConfirmOpen;

  const handleSave = () => {
    if (loadingImages) return;

    const editingTextDelta = JSON.stringify(
      editorText.current?.getQuill()?.getEditor().getContents()
    );

    if (!editingText || editorText.current?.isEmpty()) return;

    const content: any = { text: editingText, delta: editingTextDelta };
    if (quotedRemoved) content.quoted_id = null;
    content.uploads = [
      ...comment.uploads.map((u: any) => u.uuid),
      ...images.filter((el) => el.response).map((el) => el.response?.uuid),
    ];

    if (props.parentEl) {
      props.onUpdate(
        comment.uuid,
        props.parentEl.uuid,
        content,
        getMentionUuidFromNode(editingText),
        () => onEdit(false)
      );
    } else {
      props.onUpdate(
        comment.uuid,
        content,
        getMentionUuidFromNode(editingText),
        () => onEdit(false)
      );
    }
  };

  const handleDelete = async () => {
    if (props.parentEl) {
      await props.onDelete(comment.uuid, props.parentEl.uuid);
    } else {
      await props.onDelete(comment.uuid);
    }
  };

  const handleReplyInThread = () => {
    const mentionReply = comment.type !== 'tasks' || comment.creator.type !== 'contact';
    let targetReply = comment.creator.uuid !== user.uuid ? comment.creator : null;
    if (!targetReply && props.parentEl && props.parentEl.creator.uuid !== user.uuid) {
      targetReply = props.parentEl.creator;
    }
    onReplyTo((props.parentEl ?? comment).uuid, comment, mentionReply ? targetReply : null);
  };

  const addReactionHandler = (reaction: any) => {
    if (props.parentEl) {
      props.onAddReaction(props.parentEl.uuid, comment.uuid, reaction);
    } else {
      props.onAddReaction(comment.uuid, reaction);
    }
  };

  const removeReactionHandler = (reaction: any) => {
    if (props.parentEl) {
      props.onRemoveReaction(props.parentEl.uuid, comment.uuid, reaction);
    } else {
      props.onRemoveReaction(comment.uuid, reaction);
    }
  };

  const moreMenu = (
    <Menu>
      {!comment.send_at && (
        <Menu.Item
          key="quote"
          icon={<LuReply size={16} />}
          onClick={() => { onQuoteTo(comment.uuid, comment); setIsMoreDropdownOpen(false); }}
        >
          Quote in reply
        </Menu.Item>
      )}
      {isOwner && !comment.deleted_at && [
        <Menu.Divider key="divider" />,
        <Menu.Item key="delete" style={{ color: '#ff4d4f' }} className="no-hover-highlight">
          <Popconfirm
            cancelText="No"
            okText="Yes"
            onConfirm={handleDelete}
            onCancel={() => {
              setIsDeleteConfirmOpen(false);
              setTimeout(() => setIsMoreDropdownOpen(false));
            }}
            placement="bottom"
            title="Do you want to delete this comment?"
            open={isDeleteConfirmOpen}
            onOpenChange={(v) => {
              setIsDeleteConfirmOpen(v);
              if (!v) setTimeout(() => setIsMoreDropdownOpen(false));
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#ff4d4f' }}>
              <DeleteOutlined style={{ fontSize: 16 }} />
              <span>Delete</span>
            </div>
          </Popconfirm>
        </Menu.Item>,
      ]}
    </Menu>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'end', gap: 12, marginTop: 6 }}>
        {editing && (
          <button
            type="button"
            className="text-[#0044cc] default-button"
            style={{ padding: 0, marginTop: 6 }}
            onClick={handleSave}
          >
            Save
          </button>
        )}

        {editing && (
          <button
            type="button"
            className="text-red-500 default-button"
            style={{ padding: 0, marginTop: 6 }}
            onClick={() => {
              onEdit(false);
            }}
          >
            Cancel
          </button>
        )}
      </div>

      {!editing && (
        <div className="reactions-row reactions-container" style={{ minHeight: '34px' }} onClick={(e) => e.stopPropagation()}>
          <CommentReactionsCounter
            data={comment}
            onAddReaction={addReactionHandler}
            onRemoveReaction={removeReactionHandler}
          >
            {!comment.deleted_at && (
              <div className="flex items-center" style={{ gap: 6 }}>
                <CommentReactionOptions
                  data={comment}
                  onAddReaction={addReactionHandler}
                  onRemoveReaction={removeReactionHandler}
                  onVisibleChange={setIsReactionPopoverOpen}
                />
                <button
                  type="button"
                  className="default-button action-hover-container"
                  onClick={handleReplyInThread}
                >
                  Reply
                </button>
              </div>
            )}
          </CommentReactionsCounter>

          
        </div>
      )}

      {!editing && !comment.deleted_at && (
        <div
          className={`${isBarPinned ? '' : 'action-hover-container'} action-buttons-container`}
          style={isBarPinned ? { display: 'flex' } : undefined}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="action-buttons-wrapper">
            <CommentQuickReactions
              comment={comment}
              parentEl={props.parentEl}
              onAddReaction={props.onAddReaction}
              onRemoveReaction={props.onRemoveReaction}
            />
          </div>

          <div className="action-buttons-wrapper">
            <CommentReactionOptions
              data={comment}
              onAddReaction={addReactionHandler}
              onRemoveReaction={removeReactionHandler}
              onVisibleChange={setIsReactionPopoverOpen}
              alwaysShow
            />

            {isOwner && (
              <Tooltip title="Edit">
                <button
                  type="button"
                  className="default-button action-bar-icon-button"
                  onClick={() => onEdit(true)}
                >
                  <EditOutlined style={{ fontSize: 16 }} />
                </button>
              </Tooltip>
            )}

            {!comment.send_at && (
              <Tooltip title="Reply in thread">
                <button
                  type="button"
                  className="default-button action-bar-icon-button"
                  onClick={handleReplyInThread}
                >
                  <BiChat size={16} />
                </button>
              </Tooltip>
            )}

            <Dropdown
              overlay={moreMenu}
              trigger={['click']}
              placement="bottomRight"
              open={isMoreDropdownOpen || isDeleteConfirmOpen}
              onOpenChange={(v) => {
                if (!isDeleteConfirmOpen) setIsMoreDropdownOpen(v);
              }}
            >
              <Tooltip title="More actions">
                <button
                  type="button"
                  className="default-button action-bar-icon-button"
                >
                  <MoreOutlined style={{ fontSize: 16 }} />
                </button>
              </Tooltip>
            </Dropdown>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommentActions;
